/**
 * Qidiruv va filtr (TZ §10.6).
 *
 * Filtr holati URL'da turadi — sahifani ulashish mumkin va orqaga
 * tugmasi tabiiy ishlaydi.
 */

import { t, tp } from "../i18n";
import { useEffect, useRef, useState } from "react";
import { api, getCached, searchQueryToParams, type SearchQuery } from "../api";
import { CardList, CardListSkeleton, Chip, EmptyState, ErrorView } from "../components";
import { IconFilter } from "../icons";
import {
  fieldLabel, fundingLabel, levelLabel, typeLabel,
} from "../format";
import { navigate } from "../router";
import { haptic } from "../telegram";
import type { FeedResponse, OpportunityCard, TaxonomyResponse } from "../types";

/**
 * Sahifalash: avval 30 ta, keyin "Ko'rishni davom ettirish" har
 * bosilganda yana 10 ta (server bir so'rovda 50 tagacha beradi).
 * Birinchi sahifa katta — ko'pchilik shu yerda topadi; keyingilari
 * kichik — tez keladi va sahifa birdan cho'zilib ketmaydi.
 */
const FIRST_PAGE = 30;
const MORE_PAGE = 10;

const SORTS: { key: NonNullable<SearchQuery["sort"]>; label: string }[] = [
  { key: "match", label: t("search.sortMatch") },
  { key: "deadline", label: t("search.sortDeadline") },
  { key: "newest", label: t("search.sortNew") },
];

export default function Search({
  query,
  active = true,
}: {
  query: SearchQuery;
  active?: boolean;
}) {
  const [text, setText] = useState(query.q ?? "");
  const searchCacheKey = `search:${searchQueryToParams({ ...query, limit: FIRST_PAGE }).toString()}`;
  const [data, setData] = useState<FeedResponse | null>(() => getCached<FeedResponse>(searchCacheKey));
  const [taxonomy, setTaxonomy] = useState<TaxonomyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Yangi so'rov ketayotganda eski natijalar xiralashadi (pastdagi effekt)
  const [loading, setLoading] = useState(false);
  // "Ko'rishni davom ettirish" bilan qo'shilgan grantlar (birinchi 30 dan keyin)
  const [more, setMore] = useState<OpportunityCard[]>([]);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  // Javob kelguncha filtr o'zgarsa, eski filtrning grantlari yangisiga
  // qo'shilib ketmasin — javob shu kalit bilan solishtiriladi
  const queryKeyRef = useRef(searchCacheKey);
  queryKeyRef.current = searchCacheKey;
  const [showFilters, setShowFilters] = useState(false);
  const [hasBeenActive, setHasBeenActive] = useState(active);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (active && !hasBeenActive) {
      setHasBeenActive(true);
    }
  }, [active, hasBeenActive]);

  useEffect(() => {
    setText(query.q ?? "");
  }, [query.q]);

  // Jonli qidiruv: 250ms debounce
  useEffect(() => {
    if (!isTypingRef.current) return;
    const timer = setTimeout(() => {
      if ((query.q ?? "") !== text.trim()) {
        update({ q: text.trim() || undefined });
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    if (!hasBeenActive) return;
    api.taxonomy().then(setTaxonomy).catch(() => setTaxonomy(null));
  }, [hasBeenActive]);

  useEffect(() => {
    if (!hasBeenActive) return;
    let cancelled = false;
    const cached = getCached<FeedResponse>(searchCacheKey);
    // Kesh bo'lmasa ESKI natijalar joyida qoladi (xira, `loading`),
    // skeletga almashmaydi: har filtr bosilganda sahifa ~600px
    // qisqarib, keyin qaytib cho'zilardi — kartalar miltillab, ko'z
    // ostidagi joy yo'qolardi. Birinchi ochilishda `data` bo'sh, u
    // holda skelet chiqadi (pastda).
    // Qo'shimcha grantlar eski filtrga tegishli — yangi natija bilan
    // BIRGA tozalanadi (oldinroq emas: aks holda sahifa qisqarib sakraydi)
    if (cached) {
      setData(cached);
      setMore([]);
    }
    setMoreFailed(false);
    setLoading(!cached);
    setError(null);
    api
      .search({ ...query, limit: FIRST_PAGE })
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setMore([]);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoading(false);
        if (!cached) {
          // Eski natijalar boshqa filtrga tegishli — xato bilan birga
          // ko'rsatilsa, odam ularni yangi filtr natijasi deb o'ylardi
          setData(null);
          setMore([]);
          setError(err instanceof Error ? err.message : t("search.failed"));
        }
      });
    return () => {
      cancelled = true;
    };
    // `hasBeenActive` SHART: bo'lim ilova ochilganda yashirin holda
    // yaratiladi va bu effekt hech narsa qilmay qaytadi. Panel orqali
    // bo'sh qidiruvga kirilganda `query` o'zgarmaydi ({} -> {}) — usiz
    // effekt qayta ishlamas va sahifa skeletda abadiy qolardi.
  }, [JSON.stringify(query), hasBeenActive]);

  function update(patch: Partial<SearchQuery>) {
    const next: SearchQuery = { ...query, ...patch };
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "" || value === false) {
        delete next[key as keyof SearchQuery];
      }
    }
    navigate({ name: "search", query: next });
  }

  function toggle(key: "no_ielts" | "no_experience" | "no_fee") {
    update({ [key]: query[key] ? undefined : true } as Partial<SearchQuery>);
  }

  const activeCount = Object.keys(query).filter(
    (k) => k !== "q" && k !== "sort" && k !== "offset" && k !== "limit",
  ).length;

  // Ko'rsatiladigan ro'yxat. Takror `id` tashlanadi: sahifalar orasida
  // baza o'zgarsa (yangi grant qo'shilsa) bitta grant ikki sahifaga
  // tushib qolishi mumkin — u ro'yxatda ikki marta chiqmasin.
  const items: OpportunityCard[] = [];
  if (data) {
    const seen = new Set<number>();
    for (const item of [...data.items, ...more]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }
  // Serverdagi o'rin — takrorlar ham hisobga kiradi, aks holda keyingi
  // sahifa o'sha grantni yana boshidan berardi
  const fetched = (data?.items.length ?? 0) + more.length;
  const hasMore = Boolean(data) && fetched < (data?.total ?? 0);

  async function loadMore() {
    if (!data || moreBusy) return;
    haptic("light");
    const key = searchCacheKey;
    setMoreBusy(true);
    setMoreFailed(false);
    try {
      const page = await api.search({ ...query, offset: fetched, limit: MORE_PAGE });
      // Kutilayotganda filtr o'zgargan bo'lsa — bu javob endi kerak emas
      if (queryKeyRef.current !== key) return;
      setMore((cur) => [...cur, ...page.items]);
    } catch {
      if (queryKeyRef.current === key) setMoreFailed(true);
    } finally {
      setMoreBusy(false);
    }
  }

  return (
    <div className="screen">
      <form
        className="search-form"
        onSubmit={(e) => {
          e.preventDefault();
          update({ q: text.trim() || undefined });
        }}
      >
        <input
          className="input"
          value={text}
          placeholder={t("search.placeholder")}
          onChange={(e) => {
            isTypingRef.current = true;
            setText(e.target.value);
          }}
        />
        <button className="btn btn-search" type="submit">
          {t("search.submit")}
        </button>
      </form>

      <div className="chip-row">
        <Chip active={showFilters} onClick={() => setShowFilters(!showFilters)}>
          <IconFilter size={15} />
          {t("search.filters")}{activeCount ? ` (${activeCount})` : ""}
        </Chip>
        <Chip active={Boolean(query.no_ielts)} onClick={() => toggle("no_ielts")}>
          {t("search.quickNoIelts")}
        </Chip>
        <Chip
          active={Boolean(query.no_experience)}
          onClick={() => toggle("no_experience")}
        >
          {t("search.quickNoExperience")}
        </Chip>
        <Chip active={Boolean(query.no_fee)} onClick={() => toggle("no_fee")}>
          {t("search.quickNoFee")}
        </Chip>
      </div>

      {showFilters && taxonomy ? (
        <div className="filters">
          <FilterGroup
            title={t("search.filterType")}
            options={taxonomy.types.map((x) => [x.key, typeLabel(x.key, x.label)])}
            value={query.type}
            onPick={(value) => update({ type: value })}
          />
          <FilterGroup
            title={t("search.filterLevel")}
            options={taxonomy.degree_levels.map((d) => [d, levelLabel(d)])}
            value={query.degree}
            onPick={(value) => update({ degree: value })}
          />
          <FilterGroup
            title={t("search.filterFunding")}
            options={taxonomy.funding.map((f) => [f, fundingLabel(f)])}
            value={query.funding}
            onPick={(value) => update({ funding: value })}
          />
          {/*
            Qit'a — `REGIONS` (hashtag hududlari) dan boshqa o'lchov.
            Taxonomy'dan keladi, frontend ro'yxatni qotirmaydi.
          */}
          <FilterGroup
            title={t("search.filterContinent")}
            options={(taxonomy.continents ?? []).map((c) => [c.key, c.label])}
            value={query.continent}
            onPick={(value) => update({ continent: value })}
          />
          <FilterGroup
            title={t("search.filterField")}
            options={taxonomy.fields.map((f) => [f, fieldLabel(f)])}
            value={query.field}
            onPick={(value) => update({ field: value })}
          />
          <div className="filter-group">
            <h3>{t("search.sort")}</h3>
            <div className="chip-row">
              {SORTS.map((sort) => (
                <Chip
                  key={sort.key}
                  active={(query.sort ?? "match") === sort.key}
                  onClick={() => update({ sort: sort.key })}
                >
                  {sort.label}
                </Chip>
              ))}
            </div>
          </div>
          {activeCount ? (
            <button
              className="link-btn link-clear"
              type="button"
              onClick={() => navigate({ name: "search", query: { q: query.q } })}
            >
              {t("search.clearFilters")}
            </button>
          ) : null}
        </div>
      ) : null}

      {error ? <ErrorView message={error} /> : null}
      {!error && !data ? <CardListSkeleton count={4} /> : null}

      {data ? (
        <div
          className={`results${loading ? " results-stale" : ""}`}
          aria-busy={loading || undefined}
        >
          {items.length ? (
            <>
              <p className="muted small">{tp("feed.results", data.total)}</p>
              <CardList items={items} source="search" />
              {hasMore ? (
                <div className="load-more">
                  <button
                    className="btn btn-more btn-block"
                    type="button"
                    onClick={() => void loadMore()}
                    // `disabled` EMAS: u tugmani xira qilib, "ishlamayapti"
                    // degan taassurot beradi. Takror bosishni `loadMore`
                    // o'zi to'xtatadi, yozuv esa "Yuklanmoqda…" ga o'tadi.
                    aria-busy={moreBusy || undefined}
                  >
                    {moreBusy ? t("common.loading") : t("search.loadMore")}
                  </button>
                  <p className="load-more-note muted small" aria-live="polite">
                    {moreFailed
                      ? t("search.loadMoreFailed")
                      : t("search.shownOf", { shown: items.length, total: data.total })}
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            <EmptyState
              title={t("search.empty")}
              hint={t("search.emptyHint")}
              action={
                activeCount ? (
                  <button
                    className="btn btn-clear"
                    type="button"
                    onClick={() => navigate({ name: "search", query: {} })}
                  >
                    {t("search.clearFilters")}
                  </button>
                ) : undefined
              }
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

function FilterGroup({
  title,
  options,
  value,
  onPick,
}: {
  title: string;
  options: [string, string][];
  value: string | undefined;
  onPick: (value: string | undefined) => void;
}) {
  return (
    <div className="filter-group">
      <h3>{title}</h3>
      <div className="chip-row">
        {options.map(([key, label]) => (
          <Chip
            key={key}
            active={value === key}
            onClick={() => onPick(value === key ? undefined : key)}
          >
            {label}
          </Chip>
        ))}
      </div>
    </div>
  );
}
